export type Account = {
  id: string;
  name: string;
  emailAddresses: string[];
  eligibleTasks: string[];
  configuration: Record<string, string>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  stripeConnectedAccountId: string;
  stripeCustomerId?: string;
  currentSubscriptionId?: string;
  subscriptionIds: string[];
  settings?: Record<string, string>;
  dateTimeTrialEnds?: string; // ISO datetime string when trial expires
};

export type TaskDefinitionSummary = {
  id: string;
  name: string;
  pdfTemplateS3Key?: string;
  taskType?: string;
};

export type TaskDefinition = {
  id: string;
  name: string;
  description: string;
  steps: TaskStep[];
};

export type TaskStep = {
  id: string;
  name: string;
  description: string;
  prompt: string;
  type: TaskStepType;
  inputBindings: Record<string, string>;
  outputVariable?: string;
};

export enum TaskStepType {
  DataExtraction = 'DataExtraction',
  DocumentGeneration = 'DocumentGeneration',
  Validation = 'Validation',
  Analysis = 'Analysis',
  DocumentPopulation = 'DocumentPopulation',
  SendEmail = 'SendEmail',
  HttpCreate = 'HttpCreate',
  HttpRead = 'HttpRead',
  HttpUpdate = 'HttpUpdate',
  DocuSealSubmission = 'DocuSealSubmission',
  PdfConfidenceValidation = 'PdfConfidenceValidation',
  DocumentPreparation = 'DocumentPreparation',
  TextractAnalysis = 'TextractAnalysis',
  MedicalComprehend = 'MedicalComprehend'
}

export enum TaskType {
  // AWS HealthScribe Note Template Task Types (task types are now the template names directly)
  PhysicalSoap = 'PHYSICAL_SOAP',
  HistoryAndPhysical = 'HISTORY_AND_PHYSICAL',
  Girpp = 'GIRPP',
  Birp = 'BIRP',
  Sirp = 'SIRP',
  Dap = 'DAP',
  BehavioralSoap = 'BEHAVIORAL_SOAP',
  // Legacy/Other task types
  SoapNote = 'PHYSICAL_SOAP', // Legacy alias for PHYSICAL_SOAP
  DocumentPopulation = 'DocumentPopulation'
}

/**
 * Constants for task types to avoid magic strings (matches C# TaskTypeConstants)
 */
export const TaskTypeConstants = {
  AnnualWellWomanVisit: 'Annual_Well_Woman_Visit',
  PrenatalVisit: 'Prenatal_Visit',
  InitialPrenatalVisit: 'Initial_Prenatal_Visit',
  PhysicalSoap: 'PHYSICAL_SOAP',
} as const;

/**
 * Gets the AWS HealthScribe note template for a given task type.
 * Maps task types to AWS HealthScribe-supported note templates.
 * Task types that aren't directly supported by HealthScribe are mapped to compatible templates.
 */
export const getNoteTemplateFromTaskType = (taskType: string): string => {
  // Task types that need to be mapped to AWS HealthScribe-supported templates
  const templateMapping: Record<string, string> = {
    'SoapNote': 'PHYSICAL_SOAP', // Legacy mapping
    'Annual_Well_Woman_Visit': 'PHYSICAL_SOAP', // Annual Well Woman Visit uses PHYSICAL_SOAP template
    'Prenatal_Visit': 'PHYSICAL_SOAP', // Prenatal Visit uses PHYSICAL_SOAP template
    'Initial_Prenatal_Visit': 'PHYSICAL_SOAP' // Initial Prenatal Visit uses PHYSICAL_SOAP template
  };
  
  // Return mapped template if exists, otherwise return task type (if it's a valid HealthScribe template)
  return templateMapping[taskType] || taskType || 'PHYSICAL_SOAP';
};

/**
 * Checks if a task type is a streaming note template type
 */
export const isStreamingTaskType = (taskType: string): boolean => {
  if (!taskType) return false;
  
  // Valid AWS HealthScribe note template names that support streaming
  const streamingTaskTypes = [
    'PHYSICAL_SOAP',
    'HISTORY_AND_PHYSICAL',
    'GIRPP',
    'BIRP',
    'SIRP',
    'DAP',
    'BEHAVIORAL_SOAP',
    'Annual_Well_Woman_Visit',
    'Prenatal_Visit',
    'Initial_Prenatal_Visit'
  ];
  
  return streamingTaskTypes.includes(taskType);
};

export type DocumentContent = {
  name: string;       // original file name or generated
  format: string;     // e.g. "png", "jpg"
  content: string;    // base64-encoded string
};

export type ProcessingInput = {
  id: string;                     // taskDefinition.id
  sourceType: "api";
  body: string;                   // fixed prompt
  subject: string;
  from: string;
  to: string[];
  attachments: DocumentContent[];
  metadata: Record<string, unknown>;
};

export enum ProcessingStatus {
  Pending,
  AccountIdentified,
  TasksDetermined,
  TasksExecuting,
  TasksCompleted,
  ResponseGenerated,
  Completed,
  Failed
}

export type GeneratedDocument = {
  name: string;
  contentType: string;
  content: string;   // base64
  s3Key: string;
};

export type AttachmentDto = {
  name: string;
  contentBase64: string;
  mimeType: string;
  s3Key: string;
};

export type EvidenceLink = {
  segmentId?: string;
};

export type ClinicalSummary = {
  evidenceLinks?: EvidenceLink[];
  summarizedSegment?: string;
};

export type ClinicalSection = {
  sectionName?: string;
  summary?: ClinicalSummary[];
};

export type ClinicalDocumentationContent = {
  sections?: ClinicalSection[];
};

export type ClinicalDocumentation = {
  ClinicalDocumentation?: ClinicalDocumentationContent;
};

// AWS Medical Comprehend Augmentation Types
export type CmAugmentation = {
  sessionId?: string;
  createdAt?: string;
  offsetBase?: number;
  textLen?: number;
  entities?: CmEntity[];
  codes?: CmCodes;
};

export type CmEntity = {
  beginOffset?: number;
  category?: string;
  endOffset?: number;
  id?: number;
  score?: number;
  text?: string;
  traits?: CmTrait[];
  type?: string;
  evidence?: CmEvidence[];
  beginOffsetGlobal?: number;
  endOffsetGlobal?: number;
};

export type CmTrait = {
  name?: string;
  score?: number;
};

export type CmEvidence = {
  name?: string;
  score?: number;
};

export type CmCodes = {
  icd10?: CodedItem[];
  rxnorm?: CodedItem[];
  snomed?: CodedItem[];
};

export type CodedItem = {
  code?: string;
  text?: string;
  score?: number;
};

export type HealthCareTranscribeResponse = {
  transcript?: string;
  clinicalDocumentation?: ClinicalDocumentation;
  cmAugmentation?: CmAugmentation;
};

export type TaskResult = {
  taskDefinitionId: string;
  output: string;
  success: boolean;
  error?: string;
  metadata: Record<string, unknown>;
  stepResults: any[];
  generatedDocuments?: AttachmentDto[];
  generatedNotes?: HealthCareTranscribeResponse[];
};



export type TaskRequest = {
  selectedTaskDefinitionId: string;
  account: Account;
  processingInput: ProcessingInput;
};

export type RequestStatus = 'Pending' | 'InProgress' | 'Completed' | 'Failed' | 'Cancelled';

export const RequestStatus = {
  Pending: 'Pending' as const,
  InProgress: 'InProgress' as const,
  Completed: 'Completed' as const,
  Failed: 'Failed' as const,
  Cancelled: 'Cancelled' as const
} as const;



export type DocumentS3KeyInfo = {
  s3Key: string;
  documentName: string;
};

export type LLMAnalysisEnhancement = {
  id: string;
  taskStepId: string;
  taskStepName: string;
  prompt: string;
  rawOutput: string;
  taskType?: string;
  // Strongly-typed output properties (deserialized from rawOutput based on taskType)
  wellWomanOutput?: AnnualWellWomanVisitOutput;
  prenatalOutput?: PrenatalVisitOutput;
  initialPrenatalOutput?: InitialPrenatalVisitOutput;
  historyAndPhysicalOutput?: WomensHealthHistoryAndPhysicalOutput;
  // User-edited combined SOAP note as a single string
  editedCombinedSoapNote?: string;
  // User-edited History & Physical text fields (merged with original when present)
  editedHistoryAndPhysical?: HistoryAndPhysicalTextFields;
  // History of H&P edits for learning/tracking
  historyAndPhysicalEditHistory?: HistoryAndPhysicalEdit[];
  success: boolean;
  errorMessage?: string;
  createdAt: string;
  metadata: Record<string, string>;
};

export type UserRequest = {
  id: string;
  userId: string;
  accountId: string;
  patientId: string;
  patientName?: string; // Add patientName property
  taskDefinitionId: string;
  taskType: string;
  taskName: string;
  status: RequestStatus; // Change from string to RequestStatus
  createdAt: string;
  completedAt?: string;
  generatedDocumentS3Keys: DocumentS3KeyInfo[];
  generatedNotes: HealthCareTranscribeResponse[];
  llmAnalysisEnhancement?: LLMAnalysisEnhancement;
  processingContextId: string;
  logs: RequestLogEntry[];
  errorMessage?: string;
  errorStackTrace?: string;
  expiresAt?: string;
};

export type EhrFieldMapping = {
  fieldId: string;
  selector: string;
  attribute?: string;
  label?: string;
  valuePath?: string;
  staticValue?: string;
  fallbackValue?: string;
};

export type EhrPushConfig = {
  userRequestId: string;
  targetEhr: string;
  pageUrl?: string;
  instructions?: string;
  fields: EhrFieldMapping[];
};

export type VisitVitals = {
  systolic?: number | null;
  diastolic?: number | null;
  heartRate?: number | null;
  respiratoryRate?: number | null;
  temperatureC?: number | null;
  spO2?: number | null;
  painScore?: number | null;
  weightKg?: number | null;
  bmi?: number | null;
};

export type PatientDemographics = {
  dateOfBirthIso?: string | null;
  sexAtBirth?: 'Unknown' | 'Female' | 'Male' | 'Intersex' | string | null;
  genderIdentity?: string | null;
  heightCm?: number | null;
};

export type PatientVisitSummary = {
  userRequestId: string;
  visitDate: string;
  summary: string;
  streamSessionId?: string | null;
  capturedAt?: string;
  updatedAt?: string;
  vitals?: VisitVitals | null;
  lmpDateIso?: string | null;
  visitType?: string | null;
  visitContextWellWoman?: WellWomanVisitSummaryContext;
  visitContextPrenatal?: PrenatalVisitSummaryContext;
  visitContextInitialPrenatal?: InitialPrenatalVisitSummaryContext;
};

export type Patient = {
  id: string;
  userId: string;
  accountId: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  createdAt: string;
  lastSeen: string;
  demographics?: PatientDemographics | null;
  visitSummaries?: PatientVisitSummary[];
  womensHealthProfile?: WomensHealthPatientProfile;
};

export type RequestLogEntry = {
  time: string;
  level: string;
  message: string;
  exception?: string;
  category?: string;
  stepId?: string;
  s3Key?: string;
  metadata?: Record<string, string>;
};

// Stripe-related types
export type SubscriptionStatus = 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid';

export type Subscription = {
  id: string;
  status: SubscriptionStatus;
  priceId?: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd?: Date;
  connectedAccountId?: string;
  applicationFeePercent?: number;
};

export type PricingPlan = {
  id: string;
  name: string;
  description: string;
  price: number; // in cents
  priceId: string;
  features: string[];
  isPopular?: boolean;
};

export type CreateCheckoutSessionRequest = {
  accountId: string;
  priceId: string;
  successUrl: string;
  cancelUrl: string;
  allowPromoCodes?: boolean;
  applicationFeePercentOverride?: number;
};

export type CreatePortalSessionRequest = {
  accountId: string;
  returnUrl: string;
};

// Stripe Connected Account types
export type StripeConnectedAccount = {
  id: string;
  name: string;
  taskDefinitionIds: string[];
};

export type StripeConnectedAccountResponse = {
  id: string;
  name: string;
  taskDefinitionIds: string[];
  products: ProductWithPrices[];
};

export type ProductWithPrices = {
  product: StripeProduct;
  prices: StripePrice[];
};

// These types match the Stripe SDK models
export type StripeProduct = {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  created: number; // Unix timestamp
  defaultPrice?: string;
  images: string[];
  livemode: boolean;
  metadata: Record<string, string>;
  object: 'product';
  packageDimensions?: any;
  shippable?: boolean;
  statementDescriptor?: string;
  taxCode?: string;
  unitLabel?: string;
  updated: number;
  url?: string;
};

export type StripePrice = {
  id: string;
  object: 'price';
  active: boolean;
  billing_scheme: string; // 'per_unit' | 'tiered'
  created: number; // Unix timestamp
  currency: string;
  custom_unit_amount?: any;
  livemode: boolean;
  lookup_key?: string;
  metadata: Record<string, string>;
  nickname?: string;
  product: string;
  recurring?: StripeRecurring;
  tax_behavior?: string;
  tiers_mode?: string;
  transform_quantity?: any;
  type: string; // 'one_time' | 'recurring'
  unit_amount?: number; // Amount in cents
  unit_amount_decimal?: string;
};

export type StripeRecurring = {
  aggregate_usage?: string;
  interval: string; // 'day' | 'week' | 'month' | 'year'
  interval_count: number;
  trial_period_days?: number;
  usage_type: string; // 'licensed' | 'metered'
};

// Stripe Connected Account constants
export const STRIPE_CONNECTED_ACCOUNT_IDS = {
  FORMSPRINT_HEALTH: 'acct_1RlJzxRbTOOMf1RN',
  BESTI_HEALTH: 'acct_1SXvOZDUBPtK1gyO',
  KINETIC_NOTES: 'acct_1SYRY5DM5IJTJ2HM'
} as const;

export const BRAND_NAMES = {
  FORMSPRINT_HEALTH: 'FormSprint Health',
  BESTI_HEALTH: 'Besti Health',
  KINETIC_SCRIBE: 'Kinetic Scribe'
} as const;

// Trial management utilities
export const isTrialExpired = (account: Account): boolean => {
  if (!account.dateTimeTrialEnds) return false;
  return new Date() > new Date(account.dateTimeTrialEnds);
};

export const getDaysRemaining = (account: Account): number => {
  if (!account.dateTimeTrialEnds) return 0;
  const trialEnd = new Date(account.dateTimeTrialEnds);
  const now = new Date();
  const diffTime = trialEnd.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

export const hasActiveSubscription = (account: Account): boolean => {
  return !!(account.currentSubscriptionId && account.subscriptionIds.length > 0);
}; 


// womens-health-llm-output.ts

// ---------- Enums (optional but recommended) ----------
export enum CodeSystem {
  ICD10 = "ICD10",
  SNOMED = "SNOMED",
  RxNorm = "RxNorm",
  CPT = "CPT",
}

export enum RecommendationCategory {
  Lab = "Lab",
  Imaging = "Imaging",
  Referral = "Referral",
  Counseling = "Counseling",
  Preventive = "Preventive",
  Documentation = "Documentation",
}

export enum ConfidenceLevel {
  High = "High",
  Moderate = "Moderate",
  Low = "Low",
}

export enum MenopausalStatus {
  Premenopausal = "Premenopausal",
  Perimenopausal = "Perimenopausal",
  Postmenopausal = "Postmenopausal",
  Unknown = "Unknown",
}

export enum HPVVaccinationStatus {
  UpToDate = "UpToDate",
  Overdue = "Overdue",
  Unknown = "Unknown",
}

export enum AbnormalFlag {
  High = "High",
  Low = "Low",
  Normal = "Normal",
  Unknown = "Unknown",
}

// ---------- Core Types ----------
export interface WomensHealthLLMOutput {
  structuredSoap: string;
  keyConcepts: CodedConcept[];
  recommendations: Recommendation[];
  patientSummary: string;
  structuredExtraction: StructuredExtraction;
  rationale: string;
}

export interface SoapNote {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
}

export interface CodedConcept {
  codeSystem: CodeSystem | "ICD10" | "SNOMED" | "RxNorm" | "CPT";
  code: string;
  label: string;
  justification: string;
}

export interface Recommendation {
  action: string;
  category:
  | RecommendationCategory
  | "Lab"
  | "Imaging"
  | "Referral"
  | "Counseling"
  | "Preventive"
  | "Documentation";
  rationale: string;
  confidence: ConfidenceLevel | "High" | "Moderate" | "Low";
}

// ---------- StructuredExtraction ----------
export interface StructuredExtraction {
  demographics: Demographics;
  vitalsAndBodyMetrics: VitalsAndBodyMetrics;
  symptoms: Symptoms;
  screeningAndPreventiveCare: ScreeningAndPreventiveCare;
  medications: Medication[];
  conditions: ConditionItem[];
  labFindings: LabFinding[];
}

export interface Demographics {
  age: number | null;
  sex: string | null;
  raceEthnicity: string | null;
  gravida: number | null;
  para: number | null;
  lastMenstrualPeriod: string | null; // ISO date or stated text if provided
  menstrualCycleRegular: boolean | null;
  averageCycleLengthDays: number | null;
  bleedingVolume: "Light" | "Normal" | "Heavy" | null;
  intermenstrualBleeding: boolean | null;
  menopausalStatus: MenopausalStatus | "Premenopausal" | "Perimenopausal" | "Postmenopausal" | "Unknown" | null;
  hormoneTherapyUse: boolean | null;
  contraceptiveMethod: string | null;
  fertilityDesire: boolean | null;
  sexuallyActive: boolean | null;
}

export interface VitalsAndBodyMetrics {
  heightCm: number | null;
  weightKg: number | null;
  bmi: number | null;
  bloodPressureSystolic: number | null;
  bloodPressureDiastolic: number | null;
  heartRate: number | null;
}

export interface Symptoms {
  pelvicPain: boolean | null;
  vaginalBleeding: boolean | null;
  vaginalDischarge: boolean | null;
  hotFlashes: boolean | null;
  moodChanges: boolean | null;
  sleepDisturbance: boolean | null;
  urinarySymptoms: boolean | null;
  sexualDysfunction: boolean | null;
  breastSymptoms: boolean | null;
  abdominalBloating: boolean | null;
}

export interface ScreeningAndPreventiveCare {
  cervicalCancerScreeningUpToDate: boolean | null;
  mammographyUpToDate: boolean | null;
  boneDensityUpToDate: boolean | null;
  colorectalScreeningUpToDate: boolean | null;
  hpvVaccinationStatus: HPVVaccinationStatus | "UpToDate" | "Overdue" | "Unknown" | null;
  depressionScreeningPerformed: boolean | null;
  contraceptiveCounselingCompleted: boolean | null;
  domesticViolenceScreening: boolean | null;
}

export interface Medication {
  name: string;
  rxNormCode?: string | null;
  dose?: string | null;
  route?: string | null;
  frequency?: string | null;
  indication?: string | null;
}

export interface ConditionItem {
  name: string;
  icd10Code?: string | null;
  status?: "Active" | "Resolved" | "Historical" | string | null;
  onset?: string | null; // ISO or stated text if provided
  notes?: string | null;
}

export interface LabFinding {
  testName: string;
  resultValue?: string | number | null;
  units?: string | null;
  abnormalFlag?: AbnormalFlag | "High" | "Low" | "Normal" | "Unknown" | null;
}

// ---------- Helper: type guard (optional) ----------
export function isWomensHealthLLMOutput(x: any): x is WomensHealthLLMOutput {
  return (
    x &&
    typeof x === "object" &&
    typeof x.structuredSoap === "string" &&
    Array.isArray(x.keyConcepts) &&
    Array.isArray(x.recommendations) &&
    typeof x.patientSummary === "string" &&
    x.structuredExtraction &&
    x.structuredExtraction.demographics // Women's Health specific
  );
}

// ---------- Kinetic Notes (PT/Chiro) Types ----------
export enum KineticRecommendationCategory {
  Exercise = "Exercise",
  Ergonomics = "Ergonomics",
  ManualTherapy = "ManualTherapy",
  ReExam = "ReExam",
  Referral = "Referral",
  Imaging = "Imaging",
  Education = "Education",
}

export enum SpinalRegion {
  Cervical = "Cervical",
  Thoracic = "Thoracic",
  Lumbar = "Lumbar",
  Pelvis = "Pelvis",
  Extremity = "Extremity",
}

export interface KineticNotesLLMOutput {
  structuredSoap: string;
  keyConcepts: CodedConcept[];
  recommendations: KineticRecommendation[];
  patientSummary: string;
  structuredExtraction: KineticStructuredExtraction;
  rationale: string;
}

export interface KineticRecommendation {
  action: string;
  category:
    | KineticRecommendationCategory
    | "Exercise"
    | "Ergonomics"
    | "ManualTherapy"
    | "ReExam"
    | "Referral"
    | "Imaging"
    | "Education";
  rationale: string;
  confidence: ConfidenceLevel | "High" | "Moderate" | "Low";
}

export interface KineticStructuredExtraction {
  painDetails: PainDetails;
  physicalFindings: PhysicalFindings;
  treatmentsPerformed: TreatmentPerformed[];
  patientGoals: string | null;
  followUpPlan: FollowUpPlan;
}

export interface PainDetails {
  location: string | null;
  quality: string | null;
  severityNumeric: number | null;
  aggravatingFactors: string | null;
  relievingFactors: string | null;
  duration: string | null;
  functionalImpact: string | null;
}

export interface PhysicalFindings {
  postureObservations: string | null;
  rangeOfMotionFindings: string | null;
  palpationFindings: string | null;
  segmentalJointDysfunction: SegmentalJointDysfunction[];
  orthopedicTestsPerformed: string | null;
  neurologicFindings: string | null;
}

export interface SegmentalJointDysfunction {
  region: SpinalRegion | "Cervical" | "Thoracic" | "Lumbar" | "Pelvis" | "Extremity";
  levelOrJoint: string | null;
  motionRestriction: string | null;
  tendernessOrTone: string | null;
}

export interface TreatmentPerformed {
  technique: string | null;
  region: string | null;
  cptCode: string | null;
}

export interface FollowUpPlan {
  visitFrequency: string | null;
  nextVisitDate: string | null;
}

// ---------- Helper: type guard for Kinetic Notes ----------
export function isKineticNotesLLMOutput(x: any): x is KineticNotesLLMOutput {
  return (
    x &&
    typeof x === "object" &&
    typeof x.structuredSoap === "string" &&
    Array.isArray(x.keyConcepts) &&
    Array.isArray(x.recommendations) &&
    typeof x.patientSummary === "string" &&
    x.structuredExtraction &&
    x.structuredExtraction.painDetails // Kinetic Notes specific
  );
}

// ---------- Women's Health History and Physical Types ----------
export interface WomensHealthHistoryAndPhysicalOutput {
  historyAndPhysical: HistoryAndPhysical;
  keyConcepts: CodedConcept[];
  recommendations: Recommendation[];
  patientSummary: string;
  structuredExtraction: StructuredExtraction;
  rationale: string;
  visitSummary: VisitSummary;
}

export interface HistoryAndPhysical {
  chiefComplaint: string;
  historyOfPresentIllness: string;
  reviewOfSystems: string;
  pastMedicalHistory: string;
  pastSurgicalHistory: string;
  medications: Medication[];
  allergies: Allergy[];
  obGynHistory: ObGynHistory;
  familyHistory: string;
  socialHistory: string;
  physicalExam: PhysicalExam;
  diagnosticTesting: string;
  assessment: string;
  problemList: ProblemListItem[];
  plan: Plan;
  keyAbnormalFindings: KeyAbnormalFinding[];
}

/**
 * Text fields in HistoryAndPhysical that can be directly edited
 */
export type HistoryAndPhysicalTextField = 
  | 'chiefComplaint'
  | 'historyOfPresentIllness'
  | 'reviewOfSystems'
  | 'pastMedicalHistory'
  | 'pastSurgicalHistory'
  | 'familyHistory'
  | 'socialHistory'
  | 'diagnosticTesting'
  | 'assessment';

/**
 * Represents a single field edit in a History & Physical note
 */
export interface HistoryAndPhysicalFieldEdit {
  field: HistoryAndPhysicalTextField;
  before: string;
  after: string;
}

/**
 * Represents a complete edit session for a History & Physical note
 */
export interface HistoryAndPhysicalEdit {
  id: string;
  timestamp: string;
  edits: HistoryAndPhysicalFieldEdit[];
  /** LLM-generated instruction based on the edit pattern */
  generatedInstruction?: string;
}

/**
 * Partial H&P with only text fields for editing purposes
 */
export interface HistoryAndPhysicalTextFields {
  chiefComplaint?: string;
  historyOfPresentIllness?: string;
  reviewOfSystems?: string;
  pastMedicalHistory?: string;
  pastSurgicalHistory?: string;
  familyHistory?: string;
  socialHistory?: string;
  diagnosticTesting?: string;
  assessment?: string;
}

export interface Allergy {
  substance?: string | null;
  reaction?: string | null;
  severity?: string | null;
}

export interface ObGynHistory {
  gravida?: number | null;
  para?: number | null;
  abortions?: number | null;
  living?: number | null;
  lastMenstrualPeriod?: string | null;
  menstrualHistory: string;
  contraception: string;
  sexualHistory: string;
  obstetricHistory: string;
  gynecologicHistory: string;
}

export interface PhysicalExam {
  vitals: PhysicalExamVitals;
  basic: BasicExam;
}

export interface PhysicalExamVitals {
  bloodPressureSystolic?: number | null;
  bloodPressureDiastolic?: number | null;
  heartRate?: number | null;
  respiratoryRate?: number | null;
  temperatureC?: number | null;
  spO2?: number | null;
  heightCm?: number | null;
  weightKg?: number | null;
  bmi?: number | null;
}

export interface BasicExam {
  constitutional: Constitutional;
  hent: HENT;
  eyes: Eyes;
  neck: Neck;
  cardiovascular: Cardiovascular;
  pulmonary: Pulmonary;
  abdominal: Abdominal;
  genitourinaryAnorectal: GenitourinaryAnorectal;
  musculoskeletal: Musculoskeletal;
  skin: Skin;
  neurologic: Neurologic;
  psychiatricBehavioral: PsychiatricBehavioral;
}

export interface Constitutional {
  alert?: boolean | null;
  normalWeight?: boolean | null;
  normalAppearance?: boolean | null;
  obese?: boolean | null;
  acuteDistress?: boolean | null;
  illAppearing?: boolean | null;
  toxicAppearing?: boolean | null;
  diaphoretic?: boolean | null;
}

export interface HENT {
  headNormocephalic?: boolean | null;
  headAtraumatic?: boolean | null;
  rightTmNormal?: boolean | null;
  rightCanalNormal?: boolean | null;
  rightExternalEarNormal?: boolean | null;
  rightImpactedCerumen?: boolean | null;
  leftTmNormal?: boolean | null;
  leftCanalNormal?: boolean | null;
  leftExternalEarNormal?: boolean | null;
  leftImpactedCerumen?: boolean | null;
  noseNormal?: boolean | null;
  nasalCongestion?: boolean | null;
  rhinorrhea?: boolean | null;
  mouthMoist?: boolean | null;
  mouthDry?: boolean | null;
  oropharynxClear?: boolean | null;
  oropharyngealExudate?: boolean | null;
  oropharyngealErythema?: boolean | null;
}

export interface Eyes {
  perrl?: boolean | null;
  eomIntact?: boolean | null;
  conjunctivaeNormal?: boolean | null;
  rightEyeDischarge?: boolean | null;
  leftEyeDischarge?: boolean | null;
  scleralIcterus?: boolean | null;
}

export interface Neck {
  rangeOfMotionNormal?: boolean | null;
  supple?: boolean | null;
  neckRigidity?: boolean | null;
  neckTenderness?: boolean | null;
  cervicalAdenopathy?: boolean | null;
  carotidBruit?: boolean | null;
}

export interface Cardiovascular {
  rateNormal?: boolean | null;
  tachycardia?: boolean | null;
  bradycardia?: boolean | null;
  rhythmRegular?: boolean | null;
  rhythmIrregular?: boolean | null;
  pulsesNormal?: boolean | null;
  heartSoundsNormal?: boolean | null;
  murmur?: boolean | null;
  gallop?: boolean | null;
  frictionRub?: boolean | null;
}

export interface Pulmonary {
  effortNormal?: boolean | null;
  respiratoryDistress?: boolean | null;
  wheezes?: boolean | null;
  rales?: boolean | null;
  breathSoundsNormal?: boolean | null;
  stridor?: boolean | null;
  rhonchi?: boolean | null;
  chestTenderness?: boolean | null;
}

export interface Abdominal {
  abdomenFlat?: boolean | null;
  bowelSoundsNormal?: boolean | null;
  abdomenSoft?: boolean | null;
  abdominalDistension?: boolean | null;
  abdominalTenderness?: boolean | null;
  abdominalGuarding?: boolean | null;
  rightCvaTenderness?: boolean | null;
  leftCvaTenderness?: boolean | null;
  abdominalMass?: boolean | null;
  hernia?: boolean | null;
  reboundTenderness?: boolean | null;
}

export interface GenitourinaryAnorectal {
  vulvaNormal?: boolean | null;
  rectumNormal?: boolean | null;
  guaiacLessThan2Seconds?: boolean | null;
  guaiacTwoToThreeSeconds?: boolean | null;
  guaiacGreaterThan3Seconds?: boolean | null;
  vaginalDischarge?: boolean | null;
}

export interface Musculoskeletal {
  rangeOfMotionNormal?: boolean | null;
  swelling?: boolean | null;
  deformity?: boolean | null;
  rightLowerExtremityEdema?: boolean | null;
  leftLowerExtremityEdema?: boolean | null;
  musculoskeletalTenderness?: boolean | null;
  injury?: boolean | null;
}

export interface Skin {
  warm?: boolean | null;
  dry?: boolean | null;
  jaundiced?: boolean | null;
  bruising?: boolean | null;
  lesion?: boolean | null;
  pale?: boolean | null;
  erythema?: boolean | null;
  rash?: boolean | null;
  capillaryRefillLessThan2Sec?: boolean | null;
  capillaryRefillTwoToThreeSec?: boolean | null;
  capillaryRefillGreaterThan3Sec?: boolean | null;
}

export interface Neurologic {
  noFocalDeficit?: boolean | null;
  atBaseline?: boolean | null;
  cranialNerveDeficit?: boolean | null;
  weakness?: boolean | null;
  abnormalGait?: boolean | null;
  orientedX3?: boolean | null;
  disoriented?: boolean | null;
  sensoryDeficit?: boolean | null;
  abnormalCoordination?: boolean | null;
  abnormalDtrs?: boolean | null;
}

export interface PsychiatricBehavioral {
  moodNormal?: boolean | null;
  behaviorNormal?: boolean | null;
  thoughtContentNormal?: boolean | null;
  judgmentNormal?: boolean | null;
}

export interface ProblemListItem {
  problem: string;
  status?: string | null;
  notes: string;
}

export interface Plan {
  byProblem: PlanByProblem[];
  overallPlan: string;
}

export interface PlanByProblem {
  problem: string;
  planText: string;
}

export interface KeyAbnormalFinding {
  section: string;
  system: string;
  finding: string;
}

export interface VisitSummary {
  summaryText: string;
  lmpDateIso?: string | null;
  vitals: VisitVitalsDetail;
}

export interface VisitVitalsDetail {
  systolic?: number | null;
  diastolic?: number | null;
  heartRate?: number | null;
  respiratoryRate?: number | null;
  temperatureC?: number | null;
  spO2?: number | null;
  painScore?: number | null;
  weightKg?: number | null;
  bmi?: number | null;
}

// ---------- Helper: type guard for Women's Health History and Physical ----------
export function isWomensHealthHistoryAndPhysicalOutput(x: any): x is WomensHealthHistoryAndPhysicalOutput {
  return (
    x &&
    typeof x === "object" &&
    x.historyAndPhysical &&
    typeof x.historyAndPhysical.chiefComplaint === "string" &&
    Array.isArray(x.keyConcepts) &&
    Array.isArray(x.recommendations) &&
    typeof x.patientSummary === "string" &&
    x.structuredExtraction &&
    x.visitSummary // History and Physical specific
  );
}

// ---------- Annual Well Woman Visit Types ----------
export interface AnnualWellWomanVisitOutput {
  visitType: "WellWomanAnnualGyn";
  structuredSoap: string;
  keyConcepts: CodedConcept[];
  recommendations: Recommendation[];
  patientSummary: string;
  structuredExtraction: AnnualWellWomanStructuredExtraction;
  wellWomanExtension: WellWomanExtension;
  rationale: string;
  visitSummary: AnnualWellWomanVisitSummary;
  visitSummaryContext?: WellWomanVisitSummaryContext;
  patientProfileUpdate?: WomensHealthPatientProfile;
}

export interface AnnualWellWomanStructuredExtraction {
  demographicsAndIdentity: DemographicsAndIdentity;
  menstrualHistory: MenstrualHistory;
  sexualHistory: SexualHistory;
  contraceptionAndPregnancyIntent: ContraceptionAndPregnancyIntent;
  screeningAndPreventiveCare: ScreeningAndPreventiveCare;
  vitalsAndBodyMetrics: AnnualWellWomanVitalsAndBodyMetrics;
  symptomsAndConcerns: SymptomsAndConcerns;
}

export interface DemographicsAndIdentity {
  age?: number | null;
  sexAtBirth?: string | null;
  genderIdentity?: string | null;
  menopausalStatus?: "Premenopausal" | "Perimenopausal" | "Postmenopausal" | "Unknown" | null;
}

export interface MenstrualHistory {
  lastMenstrualPeriod?: string | null;
  cycleRegularity?: "Regular" | "Irregular" | "Unknown" | null;
  averageCycleLengthDays?: number | null;
  typicalFlowDescription?: string | null;
  associatedSymptoms?: string | null;
}

export interface SexualHistory {
  sexuallyActive?: boolean | null;
  partnerGenders?: string | null;
  numberOfPartnersLast12Months?: number | null;
  newPartnerLast3Months?: boolean | null;
  barrierProtectionUse?: string | null;
  stiHistoryOrConcerns?: string | null;
}

export interface ContraceptionAndPregnancyIntent {
  currentContraceptiveMethod?: string | null;
  contraceptionSatisfaction?: string | null;
  pregnancyIntent?: string | null;
}

export interface ScreeningAndPreventiveCare {
  cervicalCancerScreeningStatus?: string | null;
  mammographyStatus?: string | null;
  stiScreeningStatus?: string | null;
  hpvVaccineHistory?: string | null;
  depressionScreeningStatus?: string | null;
  ipvOrDomesticViolenceScreeningStatus?: string | null;
  otherPreventiveCare?: string | null;
}

export interface AnnualWellWomanVitalsAndBodyMetrics {
  heightCm?: number | null;
  weightKg?: number | null;
  bmi?: number | null;
  bloodPressureSystolic?: number | null;
  bloodPressureDiastolic?: number | null;
  heartRate?: number | null;
}

export interface SymptomsAndConcerns {
  chiefComplaint?: string | null;
  symptomList?: SymptomItem[];
}

export interface SymptomItem {
  name: string;
  present: boolean;
  severity?: string | null;
  onsetOrPattern?: string | null;
}

export interface WellWomanExtension {
  papHistory?: string | null;
  mostRecentPapResult?: string | null;
  breastExamFindings?: string | null;
  pelvicExamFindings?: string | null;
  lifestyleFactorsDiscussed?: string | null;
  counselingTopics?: string[];
  guidelineScreeningsDueOrSuggested?: GuidelineScreening[];
}

export interface GuidelineScreening {
  service: string;
  reason: string;
  basedOn: "Guideline" | "PatientPreference" | "ClinicalContext";
}

export interface AnnualWellWomanVisitSummary {
  summary: string;
  lmpDateIso?: string | null;
  vitals: VisitVitalsDetail;
}

export interface WellWomanVisitSummaryContext {
  primaryConcernThisVisit?: string | null;
  keyPlanSummary?: string | null;
  actionsCompletedThisVisit?: string[];
  followUpActionItems?: string[];
  hasAbnormalVitalsFlag?: boolean;
  abnormalVitalsSummary?: string | null;
  screeningUpdatesThisVisit?: string[];
}

export interface WomensHealthPatientProfile {
  ageBand?: string | null;
  menopausalStatus?: "Premenopausal" | "Perimenopausal" | "Postmenopausal" | "Unknown" | "unknown" | null;
  gravidaPara?: string | null;
  menstrualStatus?: string | null;
  sexuallyActive?: "Yes" | "No" | "yes" | "no" | "Unknown" | "unknown" | null;
  contraceptionMethods?: string[];
  cervicalScreeningStatus?: string | null;
  lastCervicalScreeningSummary?: string | null;
  nextCervicalScreeningDue?: string | null;
  mammogramStatus?: string | null;
  lastMammogramSummary?: string | null;
  nextMammogramDue?: string | null;
  colonScreeningStatus?: string | null;
  lastColonScreeningSummary?: string | null;
  stiScreeningStatus?: string | null;
  vaccinationTags?: string[];
  familyHistoryRiskTags?: string[];
  chronicGynConditionTags?: string[];
}

// ---------- Prenatal Visit Types ----------
export interface PrenatalVisitOutput {
  structuredSoap: string;
  keyConcepts: CodedConcept[];
  recommendations: Recommendation[];
  patientSummary: string;
  structuredExtraction: PrenatalStructuredExtraction;
  prenatalVisitExtension: PrenatalVisitExtension;
  visitSummary: PrenatalVisitSummary;
  visitSummaryContext?: PrenatalVisitSummaryContext;
  patientProfileUpdate?: PrenatalPatientProfileUpdate;
}

export interface PrenatalStructuredExtraction {
  demographicsAndIdentity: DemographicsAndIdentity;
  pregnancyOverview: PregnancyOverview;
  obstetricHistory: ObstetricHistory;
  currentPregnancyStatus: CurrentPregnancyStatus;
  screeningAndPrenatalTests: ScreeningAndPrenatalTests;
  vaccinationsThisPregnancy: VaccinationsThisPregnancy;
  vitalsAndBodyMetrics: PrenatalVitalsAndBodyMetrics;
  symptomsAndConcerns: SymptomsAndConcerns;
  medicationsAndAllergies: MedicationsAndAllergies;
  socialAndBehavioralFactors: SocialAndBehavioralFactors;
}

export interface PregnancyOverview {
  gestationalAgeWeeks?: number | null;
  gestationalAgeDisplay?: string | null;
  estimatedDueDateIso?: string | null;
  gravidaPara?: string | null;
  multipleGestation?: boolean | null;
  numberOfFetuses?: number | null;
  pregnancyRiskLevel?: string | null;
  highRiskReasons?: string[];
}

export interface ObstetricHistory {
  priorPregnanciesSummary?: string | null;
  priorComplications?: string[];
  priorDeliveryModes?: string[];
  otherRelevantGynecologicHistory?: string | null;
}

export interface CurrentPregnancyStatus {
  primaryObProblemList?: string[];
  fetalHeartRateBpm?: number | null;
  fundalHeightCm?: number | null;
  fundalHeightComment?: string | null;
  fetalMovementDescription?: string | null;
  contractionsDescription?: string | null;
  vaginalBleedingDescription?: string | null;
  leakageOfFluidDescription?: string | null;
  preeclampsiaSymptomsDescription?: string | null;
  otherSymptomsThisVisit?: string | null;
}

export interface ScreeningAndPrenatalTests {
  aneuploidyScreeningStatus?: string | null;
  carrierScreeningStatus?: string | null;
  diabetesScreeningStatus?: string | null;
  groupBStrepStatus?: string | null;
  anemiaScreeningStatus?: string | null;
  rhStatus?: string | null;
  rhogamStatus?: string | null;
  infectiousDiseaseScreeningSummary?: string | null;
  ultrasoundFindingsThisPregnancy?: string | null;
  labsAndImagingThisVisit?: string[];
}

export interface VaccinationsThisPregnancy {
  tdapStatus?: string | null;
  influenzaStatus?: string | null;
  covidVaccinationStatus?: string | null;
  otherVaccines?: string | null;
}

export interface PrenatalVitalsAndBodyMetrics {
  heightCm?: number | null;
  prepregnancyWeightKg?: number | null;
  currentWeightKg?: number | null;
  totalPregnancyWeightGainKg?: number | null;
  bmi?: number | null;
  bloodPressureSystolic?: number | null;
  bloodPressureDiastolic?: number | null;
  heartRate?: number | null;
  respiratoryRate?: number | null;
  temperatureC?: number | null;
  spO2?: number | null;
  urineProteinResult?: string | null;
  urineGlucoseResult?: string | null;
}

export interface MedicationsAndAllergies {
  currentMedications?: string | null;
  medicationChangesThisVisit?: string | null;
  allergies?: string | null;
}

export interface SocialAndBehavioralFactors {
  tobaccoUseStatus?: string | null;
  alcoholUseStatus?: string | null;
  substanceUseStatus?: string | null;
  intimatePartnerViolenceScreeningStatus?: string | null;
  socialSupportOrStressors?: string | null;
}

export interface PrenatalVisitExtension {
  trimester?: "First" | "Second" | "Third" | "Unknown" | null;
  visitType?: "Initial" | "Routine" | "Problem-focused" | "Other" | null;
  deliveryPlanningStatus?: string | null;
  birthPlanNotes?: string | null;
  feedingPlans?: string | null;
  prenatalCounselingTopics?: string[];
  educationAndWarningSignsReviewed?: string[];
  ordersPlacedThisVisit?: string[];
  referralsThisVisit?: string[];
  nextRecommendedVisitTiming?: string | null;
}

export interface PrenatalVisitSummary {
  summary: string;
  gestationalAgeWeeks?: number | null;
  gestationalAgeDisplay?: string | null;
  estimatedDueDateIso?: string | null;
  vitals: VisitVitalsDetail;
  fetalStatus: FetalStatus;
}

export interface FetalStatus {
  fetalHeartRateBpm?: number | null;
  fundalHeightCm?: number | null;
  fetalMovement?: string | null;
}

export interface PrenatalVisitSummaryContext {
  primaryConcernThisVisit?: string | null;
  keyPlanSummary?: string | null;
  actionsCompletedThisVisit?: string[];
  followUpActionItems?: string[];
  hasRedFlagSymptomsFlag?: boolean;
  redFlagSummary?: string | null;
  highRiskPregnancyFlag?: boolean;
  highRiskPregnancySummary?: string | null;
  prenatalScreeningUpdatesThisVisit?: string[];
}

export interface PrenatalPatientProfileUpdate {
  ageBand?: string | null;
  gravidaPara?: string | null;
  estimatedDueDateIso?: string | null;
  currentGestationalAgeBand?: string | null;
  pregnancyRiskTags?: string[];
  chronicMedicalConditionTags?: string[];
  obstetricProblemTags?: string[];
  prenatalScreeningStatus?: PrenatalScreeningStatus;
  vaccinationTags?: string[];
  socialRiskTags?: string[];
}

export interface PrenatalScreeningStatus {
  aneuploidyScreening?: string | null;
  diabetesScreening?: string | null;
  groupBStrep?: string | null;
  anemiaScreening?: string | null;
}

// ---------- Initial Prenatal Visit Types ----------
// These are intentionally kept separate from the routine Prenatal Visit types
// to avoid coupling and allow independent evolution of the two schemas.

export interface InitialPrenatalVisitOutput {
  structuredSoap: string;
  keyConcepts: CodedConcept[];
  recommendations: Recommendation[];
  patientSummary: string;
  structuredExtraction: InitialPrenatalStructuredExtraction;
  prenatalVisitInitialExtension: InitialPrenatalVisitExtension;
  visitSummary: InitialPrenatalVisitSummary;
  visitSummaryContext?: InitialPrenatalVisitSummaryContext;
  patientProfileUpdate?: InitialPrenatalPatientProfileUpdate;
}

export interface InitialPrenatalStructuredExtraction {
  demographicsAndIdentity: InitialPrenatalDemographicsAndIdentity;
  pregnancyDatingAndOverview: PregnancyDatingAndOverview;
  currentPregnancySymptomsAndConcerns: CurrentPregnancySymptomsAndConcerns;
  obstetricAndGynecologicHistoryIntake: ObstetricAndGynecologicHistoryIntake;
  medicalSurgicalFamilyHistoryIntake: MedicalSurgicalFamilyHistoryIntake;
  medicationsAllergiesAndExposures: MedicationsAllergiesAndExposures;
  socialBehavioralAndSafetyScreening: SocialBehavioralAndSafetyScreening;
  physicalExamAndVitals: InitialPrenatalPhysicalExamAndVitals;
  ultrasoundAndFetalAssessment: UltrasoundAndFetalAssessment;
  initialLabsAndPrenatalScreeningThisPregnancy: InitialLabsAndPrenatalScreening;
  geneticScreeningCounseling: GeneticScreeningCounseling;
  immunizationsThisPregnancy: InitialPrenatalImmunizations;
  counselingAndEducationThisVisit: CounselingAndEducationThisVisit;
}

export interface InitialPrenatalDemographicsAndIdentity {
  age?: number | null;
  sexAtBirth?: string | null;
  genderIdentity?: string | null;
}

export interface PregnancyDatingAndOverview {
  pregnancyConfirmation?: string | null;
  lastMenstrualPeriodIso?: string | null;
  gestationalAgeWeeks?: number | null;
  gestationalAgeDisplay?: string | null;
  estimatedDueDateIso?: string | null;
  datingMethod?: string | null;
  gravidaPara?: string | null;
  multipleGestation?: boolean | null;
  numberOfFetuses?: number | null;
  pregnancyRiskLevel?: string | null;
  highRiskReasons?: string[];
}

export interface CurrentPregnancySymptomsAndConcerns {
  chiefComplaint?: string | null;
  symptomsThisVisit?: InitialPrenatalSymptomItem[];
  vaginalBleedingDescription?: string | null;
  pelvicOrAbdominalPainDescription?: string | null;
  otherSymptomsThisVisit?: string | null;
}

export interface InitialPrenatalSymptomItem {
  name: string;
  present: boolean;
  severity?: string | null;
  onsetOrPattern?: string | null;
}

export interface ObstetricAndGynecologicHistoryIntake {
  obstetricHistoryNarrative?: string | null;
  priorPregnancyOutcomes?: PriorPregnancyOutcome[];
  priorComplications?: string[];
  priorDeliveryModes?: string[];
  gynecologicHistory?: GynecologicHistory;
}

export interface PriorPregnancyOutcome {
  outcome?: string | null;
  year?: number | null;
  gestationalAgeAtOutcomeWeeks?: number | null;
  deliveryMode?: string | null;
  complications?: string | null;
}

export interface GynecologicHistory {
  menstrualHistorySummary?: string | null;
  priorPapHistorySummary?: string | null;
  stiHistorySummary?: string | null;
  contraceptionHistorySummary?: string | null;
  uterineOrCervicalProcedures?: string[];
}

export interface MedicalSurgicalFamilyHistoryIntake {
  chronicMedicalConditions?: string[];
  surgicalHistory?: string[];
  familyHistory?: string[];
  geneticOrHereditaryRiskSummary?: string | null;
}

export interface MedicationsAllergiesAndExposures {
  currentMedications?: string | null;
  medicationChangesThisVisit?: string | null;
  allergies?: string | null;
  teratogenOrMedicationSafetyCounseling?: string | null;
  occupationalOrEnvironmentalExposures?: string | null;
  travelExposureNotes?: string | null;
}

export interface SocialBehavioralAndSafetyScreening {
  tobaccoUseStatus?: string | null;
  alcoholUseStatus?: string | null;
  substanceUseStatus?: string | null;
  socialSupportOrStressors?: string | null;
  depressionAnxietyScreening?: DepressionAnxietyScreening;
  intimatePartnerViolenceScreening?: IntimatePartnerViolenceScreening;
}

export interface DepressionAnxietyScreening {
  screenedFlag?: boolean | null;
  toolName?: string | null;
  score?: number | null;
  resultInterpretation?: string | null;
  planIfPositive?: string | null;
}

export interface IntimatePartnerViolenceScreening {
  screenedFlag?: boolean | null;
  result?: string | null;
  safetyPlanOrResources?: string | null;
  mandatoryReportingMentioned?: boolean | null;
}

export interface InitialPrenatalPhysicalExamAndVitals {
  heightCm?: number | null;
  prepregnancyWeightKg?: number | null;
  currentWeightKg?: number | null;
  bmi?: number | null;
  bloodPressureSystolic?: number | null;
  bloodPressureDiastolic?: number | null;
  heartRate?: number | null;
  respiratoryRate?: number | null;
  temperatureC?: number | null;
  spO2?: number | null;
  pelvicExamPerformedFlag?: boolean | null;
  pelvicExamFindings?: string | null;
  breastExamFindings?: string | null;
  otherExamFindings?: string | null;
}

export interface UltrasoundAndFetalAssessment {
  ultrasoundPerformedFlag?: boolean | null;
  ultrasoundType?: string | null;
  pregnancyLocation?: string | null;
  crownRumpLengthMm?: number | null;
  fetalHeartRateBpm?: number | null;
  numberOfFetuses?: number | null;
  otherUltrasoundFindings?: string | null;
  datingConclusion?: string | null;
}

export interface InitialLabsAndPrenatalScreening {
  bloodTypeRh?: LabTestResult;
  antibodyScreen?: LabTestResult;
  cbc?: LabTestResult;
  urineCulture?: LabTestResult;
  hiv?: LabTestResult;
  syphilis?: LabTestResult;
  hepatitisB?: LabTestResult;
  hepatitisC?: LabTestResult;
  rubellaImmunity?: LabTestResult;
  varicellaImmunity?: LabTestResult;
  gonorrheaChlamydia?: LabTestResult;
  otherLabsOrderedOrReviewed?: string[];
}

export interface LabTestResult {
  value?: string | null;
  result?: string | null;
  resultSummary?: string | null;
  status?: string | null;
}

export interface GeneticScreeningCounseling {
  aneuploidyScreeningDiscussion?: string | null;
  aneuploidyScreeningStatus?: string | null;
  carrierScreeningDiscussion?: string | null;
  carrierScreeningStatus?: string | null;
  geneticsReferral?: string | null;
}

export interface InitialPrenatalImmunizations {
  influenzaStatus?: string | null;
  covidVaccinationStatus?: string | null;
  tdapStatus?: string | null;
  otherVaccinesOrImmunityNotes?: string | null;
}

export interface CounselingAndEducationThisVisit {
  prenatalVitaminsAndFolicAcid?: string | null;
  nutritionAndWeightGainCounseling?: string | null;
  exerciseCounseling?: string | null;
  substanceUseCounseling?: string | null;
  medicationSafetyCounseling?: string | null;
  warningSignsReviewed?: string[];
}

export interface InitialPrenatalVisitExtension {
  visitType?: "Initial" | string;
  trimester?: "First" | "Second" | "Third" | "Unknown" | null;
  initialIntakeSectionsCompleted?: string[];
  ordersPlacedThisVisit?: string[];
  referralsThisVisit?: string[];
  pendingResultsOrFollowUps?: string[];
  nextRecommendedVisitTiming?: string | null;
}

export interface InitialPrenatalVisitSummary {
  summary: string;
  gestationalAgeWeeks?: number | null;
  gestationalAgeDisplay?: string | null;
  estimatedDueDateIso?: string | null;
  vitals: VisitVitalsDetail;
  fetalStatus: InitialPrenatalFetalStatus;
}

export interface InitialPrenatalFetalStatus {
  fetalHeartRateBpm?: number | null;
  fetalMovement?: string | null;
}

export interface InitialPrenatalVisitSummaryContext {
  primaryConcernThisVisit?: string | null;
  datingAndViabilityStatus?: string | null;
  keyPlanSummary?: string | null;
  actionsCompletedThisVisit?: string[];
  followUpActionItems?: string[];
  pendingCriticalResultsFlag?: boolean;
  pendingCriticalResultsSummary?: string | null;
  hasRedFlagSymptomsFlag?: boolean;
  redFlagSummary?: string | null;
  highRiskPregnancyFlag?: boolean;
  highRiskPregnancySummary?: string | null;
}

export interface InitialPrenatalPatientProfileUpdate {
  ageBand?: string | null;
  gravidaPara?: string | null;
  estimatedDueDateIso?: string | null;
  currentGestationalAgeBand?: string | null;
  pregnancyRiskTags?: string[];
  chronicMedicalConditionTags?: string[];
  obstetricProblemTags?: string[];
  prenatalScreeningStatus?: InitialPrenatalScreeningStatus;
  vaccinationTags?: string[];
  socialRiskTags?: string[];
}

export interface InitialPrenatalScreeningStatus {
  aneuploidyScreening?: string | null;
  diabetesScreening?: string | null;
  groupBStrep?: string | null;
  anemiaScreening?: string | null;
}